import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { processedDocumentClassificationToLabelMap } from '~/data/schemas/documentReview';
import { BUTTON_SX } from '~/theme';

import DocumentUploadFileList from './DocumentUploadFileList';
import { EXPECTED_TYPE_OPTIONS, type UseDocumentUpload } from './useDocumentUpload';

interface DocumentUploadFieldsProps {
  upload: UseDocumentUpload;
  /** Short intro line above the controls; varies by host surface. */
  intro?: React.ReactNode;
  /**
   * ``full`` (internal tools): portfolio optional, explicit expected-type
   * checkboxes and an auto-approve switch. ``simplified`` (consultant-facing):
   * portfolio required, auto-filing tucked behind an "Advanced" disclosure.
   * ``files-only``: intro + drop zone with no auto-split config, for a host that
   * scopes the upload itself (onboarding intake, AMP-518).
   */
  variant?: 'full' | 'simplified' | 'files-only';
}

/** Portfolio picker shared by both variants; ``required`` drives the label + asterisk. */
const PortfolioSelect: React.FC<{ upload: UseDocumentUpload; required: boolean }> = ({
  upload,
  required,
}) => {
  const { portfolios, portfolio, setPortfolio } = upload;
  const label = required ? 'Portfolio' : 'Portfolio (optional)';
  return (
    <FormControl fullWidth required={required}>
      <InputLabel id="portfolio-select-label">{label}</InputLabel>
      <Select
        labelId="portfolio-select-label"
        id="portfolio-select"
        label={label}
        value={portfolio}
        onChange={(e) => setPortfolio(e.target.value)}
      >
        {portfolios.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * The AI-pipeline config: portfolio, auto-filing, and the drop zone (with the
 * per-file "Single contract" choice). Shared by the standalone
 * DocumentUploadDialog (``full``) and the Add-to-Property "AI pipeline" tab
 * (``simplified``).
 */
const DocumentUploadFields: React.FC<DocumentUploadFieldsProps> = ({
  upload,
  intro,
  variant = 'full',
}) => {
  const {
    portfolio,
    expectedTypes,
    toggleExpectedType,
    setExpectedTypes,
    autoApprove,
    setAutoApprove,
    effectiveAutoApprove,
    autoApproveDisabled,
  } = upload;

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const introBlock = intro ? (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" color="text.secondary">
        {intro}
      </Typography>
    </Box>
  ) : null;

  if (variant === 'files-only') {
    return (
      <>
        {introBlock}
        <DocumentUploadFileList upload={upload} showKeepWhole={false} />
      </>
    );
  }

  // The simplified surface hides expected types entirely: flipping auto-file on
  // targets the only auto-approvable types (invoice + ticket); off clears them.
  const handleAutoFileToggle = (checked: boolean) => {
    setExpectedTypes(checked ? [...EXPECTED_TYPE_OPTIONS] : []);
    setAutoApprove(checked);
  };

  if (variant === 'simplified') {
    return (
      <>
        {intro ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {intro}
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ mb: 3 }}>
          <PortfolioSelect upload={upload} required />
        </Box>

        <DocumentUploadFileList upload={upload} />

        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setAdvancedOpen((v) => !v)}
            endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', color: 'text.secondary', ...BUTTON_SX }}
          >
            Advanced options
          </Button>
          <Collapse in={advancedOpen}>
            <Box sx={{ pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoApprove}
                    disabled={!portfolio}
                    onChange={(e) => handleAutoFileToggle(e.target.checked)}
                  />
                }
                label="Auto-file confident invoices & tickets"
              />
              <FormHelperText sx={{ ml: 0 }}>
                {portfolio
                  ? 'High-confidence invoices and tickets are created automatically after extraction; everything else still waits for your review.'
                  : 'Select a portfolio to enable auto-filing.'}
              </FormHelperText>
            </Box>
          </Collapse>
        </Box>
      </>
    );
  }

  return (
    <>
      {intro ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {intro}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <PortfolioSelect upload={upload} required={false} />

        <FormControl component="fieldset">
          <FormLabel component="legend">
            Expected Document Types{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              · optional, targets which types get auto-approved
            </Typography>
          </FormLabel>
          <FormGroup row>
            {EXPECTED_TYPE_OPTIONS.map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={expectedTypes.includes(type)}
                    onChange={() => toggleExpectedType(type)}
                  />
                }
                label={processedDocumentClassificationToLabelMap.get(type) ?? type}
              />
            ))}
          </FormGroup>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={effectiveAutoApprove}
              disabled={autoApproveDisabled}
              onChange={(e) => setAutoApprove(e.target.checked)}
            />
          }
          label="Auto-approve confident invoices & tickets"
        />
      </Box>

      <DocumentUploadFileList upload={upload} />
    </>
  );
};

export default DocumentUploadFields;
